import styled from 'styled-components';
import Accordion from 'react-bootstrap/Accordion';
import { formatNumber } from '@utils/helpers';
import { formatArea, formatLength } from '@utils/measurementSystem';

const AccordionItem = styled(Accordion.Item)`
	border-color: ${({ theme }) => theme.colors.border};
`;

const AccordionHeader = styled(Accordion.Header)`
	.accordion-button {
		background-color: ${({ theme }) => theme.colors.modal};
		color: ${({ theme }) => theme.colors.textColor};
		margin-bottom: 0;
		box-shadow: none;

		&:after {
			background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'><path fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/></svg>") !important;
		}
	}
`;

const AccordionBody = styled(Accordion.Body)`
	padding: 0;
	margin: 0;
	background-color: ${({ theme }) => theme.colors.background};
`;

const DetectionsDetails = styled.div`
	font-weight: normal;
	line-height: 1.2;
	color: ${({ theme }) => theme.colors.textColor};

	padding: 20px;
	p {
		margin-bottom: 20px;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		&:last-of-type {
			margin-bottom: 0;
		}
	}
`;

const Name = styled.span`
	text-transform: capitalize;
	color: ${({ theme }) => theme.colors.textAccent};
	color: ${({ theme }) => theme.colors.textColor};
	font-size: 0.9em;
	font-weight: 700;
`;

const Details = styled.div`
	padding: 0 20px 20px;
	span {
		text-transform: capitalize;
		color: ${({ theme }) => theme.colors.textAccent};
	}
	p {
		margin-bottom: 10px;
		display: flex;
		flex-direction: column;
		font-size: 0.9em;
		font-weight: normal;
		line-height: 1.1;
		&:last-of-type {
			margin-bottom: 0;
		}
	}
`;

const DetailLabel = ({ label, value, show = true }) => {
	if (!show) return null;
	return (
		<p>
			<span>{label}</span>
			{value}
		</p>
	);
};

const HexagonDetails = ({
	count,
	area,
	meanHeight,
	stemsLow,
	stemsMedium,
	stemsHigh,
	stemsPerHa,
}) => {
	count = formatNumber(count, 0);
	const [areaNumber, areaUnit] = formatArea(area);
	const [meanHeightNumber, meanHeightUnit] = formatLength(meanHeight);

	return (
		<Details>
			<DetailLabel label="Objects in Total" value={count} />
			<DetailLabel label="Area" value={`${areaNumber} ${areaUnit}`} />
			<DetailLabel
				label="Mean Height of Objects"
				value={`${meanHeightNumber} ${meanHeightUnit}`}
				show={meanHeight > 0}
			/>
			{(stemsLow > 0 || stemsMedium > 0 || stemsHigh > 0) && (
				<>
					<DetailLabel
						label="Stem Estimate (Low)"
						value={formatNumber(stemsLow, 0)}
						show={stemsLow > 0}
					/>
					<DetailLabel
						label="Stem Estimate (Medium)"
						value={formatNumber(stemsMedium, 0)}
						show={stemsMedium > 0}
					/>
					<DetailLabel
						label="Stem Estimate (High)"
						value={formatNumber(stemsHigh, 0)}
						show={stemsHigh > 0}
					/>
					<DetailLabel
						label="Stem Estimate/ha (Medium)"
						value={`${formatNumber(stemsPerHa, 0)} /ha`}
						show={stemsPerHa > 0}
					/>
				</>
			)}
		</Details>
	);
};

/** Formats a string by replacing underscores with spaces and capitalizing the first letter of each word. */
const formatLabel = text => text.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const DetectionDetail = ({ detailName, detailValue }) => {
	const getLabel = { mean_height: 'Mean Height of Objects', height_std: 'Height STD' };
	const label = getLabel[detailName] ?? formatLabel(detailName);

	let unit = '';

	// If detailName is not a name, count, or mean_confidence, format it as area or length depending on if the detailName contains 'area' or 'hectare'
	if (!/name|count|mean_confidence/.test(detailName)) {
		const [convertedNumber, unitString] = /area|hectare/.test(detailName)
			? formatArea(detailValue)
			: formatLength(detailValue);

		detailValue = convertedNumber;
		unit = unitString;
	}

	// If detailName is mean_confidence, format it as a percentage
	if (detailName == 'mean_confidence') {
		detailValue = formatNumber(detailValue * 100);
		unit = '%';
	}

	const showValue = detailValue != 0 && Boolean(detailValue);
	return (
		<p>
			<Name>{label}</Name>
			<span>{showValue ? `${detailValue} ${unit}` : '-'}</span>
		</p>
	);
};

const DetectionItem = ({ id, detectionKey, detectionData }) => {
	const detectionsCount = formatNumber(detectionData?.count);

	return (
		<AccordionItem
			eventKey={`hexagon-detections-item-event-${detectionKey}`}
			key={`hexagon-detections-item-${detectionKey}`}>
			<AccordionHeader>
				{detectionKey} ({detectionsCount})
			</AccordionHeader>
			<AccordionBody>
				<DetectionsDetails>
					{Object.entries(detectionData).map(([detailName, detailValue]) => (
						<DetectionDetail
							key={`${id}-${detectionKey}-${detailName}`}
							detailName={detailName}
							detailValue={detailValue}
						/>
					))}
				</DetectionsDetails>
			</AccordionBody>
		</AccordionItem>
	);
};

export const HexagonAccordion = ({ hexagonData }) => {
	if (!hexagonData?.detections) return null;

	const {
		id,
		detections,
		metadata: {
			count,
			area,
			mean_height,
			stems_low,
			stems_medium,
			stems_high,
			stems_per_ha_medium,
		} = {},
	} = hexagonData ?? {
		detections: {},
		metadata: {
			count: 0,
			area: 0,
			mean_height: 0,
			stems_low: 0,
			stems_medium: 0,
			stems_high: 0,
			stems_per_ha_medium: 0,
		},
	};

	return (
		<>
			<HexagonDetails
				count={count}
				area={area}
				meanHeight={mean_height}
				stemsLow={stems_low}
				stemsMedium={stems_medium}
				stemsHigh={stems_high}
				stemsPerHa={stems_per_ha_medium}
			/>
			<Accordion flush>
				{Object.entries(detections).map(([detectionKey, detectionData]) => (
					<DetectionItem
						key={`hexagon-detection-${detectionKey}`}
						id={id}
						detectionKey={detectionKey}
						detectionData={detectionData}
					/>
				))}
			</Accordion>
		</>
	);
};

export default HexagonAccordion;
